import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Users,
  Award,
  ShieldCheck,
  Clock,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  CheckCircle,
  Leaf,
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: ShieldCheck,
      title: 'Quality Workmanship',
      description: 'We take pride in every project, using quality materials and proven techniques.',
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Your satisfaction is our priority. We listen to your needs and deliver results.',
    },
    {
      icon: Award,
      title: 'Professional Service',
      description: 'Our experienced team maintains high standards of professionalism.',
    },
    {
      icon: Clock,
      title: 'Reliable Scheduling',
      description: 'We show up on time and complete projects as promised.',
    },
  ];

  const serviceAreas = [
    'Seattle',
    'Bellevue',
    'Kirkland',
    'Redmond',
    'Mercer Island',
    'Issaquah',
    'Bothell',
    'Woodinville',
    'Sammamish',
    'Shoreline',
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#2E5C31]" data-testid="about-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-['Poppins']">
            About DecorVista
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
            Seattle's trusted landscaping company dedicated to transforming outdoor spaces with quality and care.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-[#2E5C31] font-semibold text-sm uppercase tracking-wider mb-3">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6 font-['Poppins']">
                Locally Owned, Community Focused
              </h2>
              <p className="text-[#4A4A4A] mb-6 leading-relaxed">
                DecorVista Landscaping Services is a locally owned and operated company serving residential homeowners throughout Seattle and the surrounding Puget Sound region. Our team brings years of experience in landscaping, with deep knowledge of the Pacific Northwest's unique climate and conditions.
              </p>
              <p className="text-[#4A4A4A] mb-6 leading-relaxed">
                We understand that your outdoor space is an extension of your home. That's why we take the time to listen to your vision, assess your property's needs, and create customized solutions that enhance your home's beauty and value.
              </p>
              <p className="text-[#4A4A4A] mb-8 leading-relaxed">
                From initial consultation to project completion, we maintain clear communication and transparent pricing, ensuring you know exactly what to expect every step of the way.
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#2E5C31] rounded-full flex items-center justify-center">
                  <Leaf className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#1A1A1A]">DecorVista Landscaping</p>
                  <p className="text-[#4A4A4A] text-sm">Serving Seattle Since Day One</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1558904541-efa843a96f01?q=80&w=800&auto=format&fit=crop"
                alt="DecorVista Team"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 md:py-28 bg-[#F2F5F3]" data-testid="values-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#2E5C31] font-semibold text-sm uppercase tracking-wider mb-3">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4 font-['Poppins']">
              What We Stand For
            </h2>
            <p className="text-[#4A4A4A] max-w-2xl mx-auto">
              Our core values guide everything we do, from how we treat our customers to how we approach each project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="bg-white p-8 rounded-2xl shadow-md text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 bg-[#2E5C31] rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3 font-['Poppins']">
                  {value.title}
                </h3>
                <p className="text-[#4A4A4A] text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-[#2E5C31] font-semibold text-sm uppercase tracking-wider mb-3">Service Areas</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6 font-['Poppins']">
                Serving Greater Seattle
              </h2>
              <p className="text-[#4A4A4A] mb-8 leading-relaxed">
                We proudly serve residential homeowners throughout the Seattle metropolitan area and surrounding communities. Our local knowledge ensures we recommend plants and solutions perfectly suited for our Pacific Northwest climate.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {serviceAreas.map((area) => (
                  <div key={area} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#2E5C31]" />
                    <span className="text-[#4A4A4A]">{area}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#888888] text-sm mt-6">
                Don't see your area? Contact us to check if we service your location.
              </p>
            </div>
            <div className="bg-[#F2F5F3] p-8 rounded-2xl">
              <h3 className="text-xl font-semibold text-[#1A1A1A] mb-6 font-['Poppins']">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2E5C31] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">Address</p>
                    <p className="text-[#4A4A4A]">667 Harbor View Drive<br />Seattle, WA 98101</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2E5C31] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">Phone</p>
                    <a href="tel:+18888808889" className="text-[#2E5C31] hover:underline">+1-888-880-8889</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2E5C31] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">Email</p>
                    <a href="mailto:support@decorvista.store" className="text-[#2E5C31] hover:underline">support@decorvista.store</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2E5C31] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">Business Hours</p>
                    <p className="text-[#4A4A4A]">Mon - Sat: 9:00 AM - 6:00 PM<br />Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#2E5C31]" data-testid="about-cta-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-['Poppins']">
            Let's Work Together
          </h2>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto mb-8">
            Ready to transform your outdoor space? Contact us today for a free consultation and estimate.
          </p>
          <Link to="/contact#estimate-form">
            <Button
              className="bg-white text-[#2E5C31] hover:bg-[#D4E157] hover:text-[#1A1A1A] rounded-full px-10 py-4 text-lg font-semibold shadow-lg transition-all duration-300"
              data-testid="about-cta-btn"
            >
              Get Free Estimate
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
